"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

/**
 * Panels in the story gallery. The clip leads when there is one: it is the story,
 * and a still of the same person next to it is the lesser version. The portrait
 * follows it, and is the only panel when a story has no clip, so index 0 is
 * whichever of the two exists.
 */
export const VIDEO_SLIDE = 0;
export const PORTRAIT_SLIDE = 1;

interface StoryStageValue {
  slide: number;
  setSlide: (slide: number) => void;
  registerVideo: (video: HTMLVideoElement | null) => void;
  registerFrame: (frame: HTMLElement | null) => void;
  /** Chapter under the playhead, or -1 before anything has played. */
  activeChapter: number;
  setActiveChapter: (index: number) => void;
  /** Swipes to the clip, seeks to `seconds`, and starts playing. */
  jumpTo: (seconds: number) => void;
}

const StoryStageContext = createContext<StoryStageValue | null>(null);

export function useStoryStage() {
  const value = useContext(StoryStageContext);
  if (!value) {
    throw new Error("useStoryStage must be used inside <StoryStage>");
  }
  return value;
}

/**
 * Holds the small amount of state the gallery and the chapter rail share: which
 * panel is showing, and where the playhead is. They sit on opposite sides of a
 * server-rendered article, so a provider is the only thing that can join them
 * without lifting the markdown into a client component.
 */
export default function StoryStage({ children }: { children: ReactNode }) {
  const [slide, setSlide] = useState(0);
  const [activeChapter, setActiveChapter] = useState(-1);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const frameRef = useRef<HTMLElement | null>(null);

  const registerVideo = useCallback((video: HTMLVideoElement | null) => {
    videoRef.current = video;
  }, []);

  const registerFrame = useCallback((frame: HTMLElement | null) => {
    frameRef.current = frame;
  }, []);

  const jumpTo = useCallback((seconds: number) => {
    setSlide(VIDEO_SLIDE);

    // The rail is sticky, so a chapter can be tapped from anywhere in the
    // article: pull the player back on screen before it starts playing out of
    // sight. `scrollIntoView` on the frame rather than the video, which sits
    // inside the horizontal track and would drag it along.
    const frame = frameRef.current;
    if (frame) {
      const { top, bottom } = frame.getBoundingClientRect();
      const margin = 120;
      if (bottom < margin || top > window.innerHeight - margin) {
        frame.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }

    const video = videoRef.current;
    if (!video) {
      return;
    }
    const seek = () => {
      video.currentTime = seconds;
      void video.play();
    };
    // A seek is silently dropped before metadata lands, which is exactly the
    // state the first chapter tap arrives in: nothing has played yet.
    if (video.readyState >= 1) {
      seek();
    } else {
      video.addEventListener("loadedmetadata", seek, { once: true });
    }
  }, []);

  const value = useMemo(
    () => ({
      slide,
      setSlide,
      registerVideo,
      registerFrame,
      activeChapter,
      setActiveChapter,
      jumpTo,
    }),
    [slide, activeChapter, registerVideo, registerFrame, jumpTo],
  );

  return (
    <StoryStageContext.Provider value={value}>
      {children}
    </StoryStageContext.Provider>
  );
}
